import {useCallback, useState, memo, useEffect} from 'react';
import {trpc, createMutationErrorHandler} from '@common-web/lib/trpc';
import {reportError} from '@common-web/lib/errorReporting';
import {Card, CardContent, CardHeader, CardTitle} from '@common-web/components/ui/card';
import {Button} from '@common-web/components/ui/button';
import {Badge} from '@common-web/components/ui/badge';
import {Alert, AlertDescription} from '@common-web/components/ui/alert';
import {Separator} from '@common-web/components/ui/separator';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@common-web/components/ui/collapsible';
import {CollapseChevron} from '@common-web/components/collapse-chevron';
import {
  CheckCircle,
  AlertCircle,
  Loader2,
  FileStack,
  Sparkles,
  Upload,
  AlertTriangle,
  NotebookPen,
  FileText,
  Download,
} from 'lucide-react';
import {ValidationResults} from './ValidationResults';
import {FileUploadDropzone} from './components/FileUploadDropzone';
import {
  PayrollAnchorField,
  PayrollFileColumnMapping,
  PayrollRowExportType,
  ValidationReport,
} from '@api/lib/payrollProcessing/types';
import {FileForUpload, FileIdValidation} from './types';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@common-web/components/ui/select';
import {Textarea} from '@common-web/components/ui/textarea';
import {exportFileValidationTransformedRowCsv} from '@client/pages/payroll/utils/exportUtils';
import {toast} from '@common-web/hooks/use-toast';
import {SkipInvalidAlert} from '@client/pages/payroll/components/SkipInvalidAlert';
import {formatFilesForProcessing} from '@client/pages/payroll/utils/fileUtils';
import {FileIdValidationAlert} from '@client/pages/payroll/components/FileIdValidationAlert';
import {Checkbox} from '@common-web/components/ui/checkbox';
import {Label} from '@common-web/components/ui/label';
import {useAuthStore} from '@common-web/stores/authStore';
import {useHasPermission} from '@common-web/hooks/use-permissions';
import {CODE_PERMISSIONS} from '@common/permissions';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@common-web/components/ui/dropdown-menu';
import {SampleDataTable} from '@client/pages/payroll/components/SampleDataTable';
import {applyFileIdSwaps, compareFileIds} from '@client/pages/payroll/utils/columnValidation';

export const PayrollUpload = memo(
  ({
    agentId,
    onUploadStart,
    config,
    files,
    onFilesChange,
    hasUploadedFiles,
    onUploadComplete,
    onResetUpload,
    onConfigureUnmapped,
    shouldAutoValidate,
    setShouldAutoValidate,
  }: {
    agentId: string;
    onUploadStart: () => void;
    config?: {
      columnMappings?: PayrollFileColumnMapping[] | null;
      anchorFields?: PayrollAnchorField[] | null;
      ignoredColumns?: string[] | null;
    };
    files: FileForUpload[];
    onFilesChange: (files: FileForUpload[]) => void;
    hasUploadedFiles: boolean;
    onUploadComplete: (uploadBatchId: string) => void;
    onResetUpload: () => void;
    onConfigureUnmapped: () => void;
    shouldAutoValidate: boolean;
    setShouldAutoValidate: (shouldAutoValidate: boolean) => void;
  }) => {
    const isInternalUser = useAuthStore((state) => state.isInternalUser());
    // Backend only honors the test-upload flag for holders of this key; without
    // it the checkbox would silently submit a real upload.
    const canMarkTestUpload = useHasPermission(CODE_PERMISSIONS.payroll.overrideTestUpload.key);
    const [validating, setValidating] = useState(false);
    const [validationReport, setValidationReport] = useState<ValidationReport | null>(null);
    const [processingStatus, setProcessingStatus] = useState<'uploading' | 'failed' | null>(null);
    const [uploadStatusMessage, setUploadStatusMessage] = useState('');
    const [uploadSectionOpen, setUploadSectionOpen] = useState(true);
    const [notesSectionOpen, setNotesSectionOpen] = useState(false);
    const [notesRequired, setNotesRequired] = useState(false);
    const [reasonForMultipleUploads, setReasonForMultipleUploads] = useState('');
    const [uploadNotes, setUploadNotes] = useState('');
    const [isTestUpload, setIsTestUpload] = useState(false);

    const [fileIdValidation, setFileIdValidation] = useState<FileIdValidation>({
      show: false,
      missingFileIds: [],
      currentFileIds: [],
      previousFileIds: [],
      mode: undefined,
      suspectedSwappedPairs: [],
    });

    const validateFiles = trpc.group.payrollProcessing.validateFiles.useMutation({
      onError: createMutationErrorHandler('Validation failed'),
    });

    useEffect(() => {
      if (isTestUpload) {
        setReasonForMultipleUploads('Testing');
      }
    }, [isTestUpload]);

    const transformForExport = trpc.group.payrollProcessing.transformFilesForExport.useMutation({
      onError: createMutationErrorHandler('Export failed'),
    });
    const uploadPayroll = trpc.group.payrollProcessing.uploadPayrollFiles.useMutation({
      onError: createMutationErrorHandler('Upload failed'),
    });

    const handleFilesChange = useCallback(
      (newFiles: FileForUpload[]) => {
        onFilesChange(newFiles);
        setValidationReport(null);
      },
      [onFilesChange],
    );

    const onCloseFileIdValidationAlert = useCallback(() => {
      setFileIdValidation({
        show: false,
        missingFileIds: [],
        currentFileIds: [],
        previousFileIds: [],
        mode: undefined,
        suspectedSwappedPairs: [],
      });
    }, []);

    const applySwappedFileIds = useCallback(() => {
      const pairs = fileIdValidation.suspectedSwappedPairs || [];
      if (!pairs || pairs.length === 0) return;
      const next = applyFileIdSwaps(files, pairs);
      handleFilesChange(next);
      onCloseFileIdValidationAlert();
    }, [
      files,
      handleFilesChange,
      fileIdValidation.suspectedSwappedPairs,
      onCloseFileIdValidationAlert,
    ]);

    const runFileIdValidation = useCallback(
      (extractedColumnsByFileId?: Record<string, string[]>): {valid: boolean} => {
        if (!config?.columnMappings || config.columnMappings.length === 0) {
          return {valid: false};
        }
        const fileIdValidationResult = compareFileIds(
          config.columnMappings,
          extractedColumnsByFileId
            ? {type: 'pre-extracted', files, columnsByFileId: extractedColumnsByFileId}
            : {type: 'base', files},
        );
        if (fileIdValidationResult.missingFileIds.length > 0) {
          setFileIdValidation({
            ...fileIdValidationResult,
            show: true,
            mode: 'missing',
            suspectedSwappedPairs: [],
          });
          return {valid: false};
        }

        if (
          fileIdValidationResult.suspectedSwappedPairs &&
          fileIdValidationResult.suspectedSwappedPairs.length > 0
        ) {
          setFileIdValidation({
            ...fileIdValidationResult,
            show: true,
            missingFileIds: [],
            mode: 'swapped',
          });
          return {valid: false};
        }

        onCloseFileIdValidationAlert();
        return {valid: true};
      },
      [config?.columnMappings, files, onCloseFileIdValidationAlert],
    );

    const runValidation = useCallback(
      async (skipFileIdValidation = false) => {
        if (files.length === 0) return;

        if (skipFileIdValidation) {
          onCloseFileIdValidationAlert();
        } else {
          const fileIdValidationResult = runFileIdValidation();
          if (!fileIdValidationResult.valid) {
            return;
          }
        }

        setValidating(true);
        try {
          const encodedFiles = await formatFilesForProcessing(files);
          const validationResult = await validateFiles.mutateAsync({agentId, files: encodedFiles});

          if (!skipFileIdValidation) {
            const fileIdValidationResult = runFileIdValidation(
              validationResult.validationReport.structuralValidation.columnsByFileId,
            );
            if (!fileIdValidationResult.valid) {
              return;
            }
          }

          setUploadSectionOpen(false);
          setValidationReport(validationResult.validationReport);
          setNotesRequired(!!validationResult?.notesRequired);
          setNotesSectionOpen(!!validationResult?.notesRequired);
          setReasonForMultipleUploads('');
          setUploadNotes('');
        } finally {
          setValidating(false);
        }
      },
      [files, agentId, validateFiles, runFileIdValidation],
    );

    const runProcessing = useCallback(async () => {
      if (!validationReport) return;
      if (notesRequired && !reasonForMultipleUploads.trim()) {
        toast({
          title: 'Upload Notes Required',
          description: `Please select the reason you are uploading more than one file in the Upload Notes section before proceeding.`,
          variant: 'destructive',
        });
        return;
      }
      if (notesRequired && uploadNotes.trim().length < 2) {
        toast({
          title: 'Upload Notes Required',
          description: `Please provide additional context in the Upload Notes section before proceeding.`,
          variant: 'destructive',
        });
        return;
      }
      setProcessingStatus('uploading');
      onUploadStart();
      try {
        const encoded = await formatFilesForProcessing(files);
        const res = await uploadPayroll.mutateAsync({
          agentId,
          files: encoded,
          reasonForMultipleUploads,
          uploadNotes,
        });
        if (res?.success) {
          onUploadComplete(res.uploadBatchId);
          setProcessingStatus(null);
          setUploadStatusMessage('');
        } else {
          setProcessingStatus('failed');
          setUploadStatusMessage(res.error || '');
        }
      } catch (e) {
        // uploadPayroll.onError toasts request failures; this also covers
        // errors thrown while encoding files before the request. The toast
        // limit of 1 prevents a visible duplicate on the request path.
        reportError(e, {
          title: 'Upload failed',
          context: 'PayrollUpload.runProcessing',
          onError: () => {
            setProcessingStatus('failed');
            setUploadStatusMessage('');
          },
        });
      }
    }, [
      files,
      agentId,
      uploadPayroll,
      validationReport,
      onUploadStart,
      reasonForMultipleUploads,
      uploadNotes,
      notesRequired,
    ]);

    const canProcess =
      !!validationReport?.summary?.canProcess &&
      (validationReport?.summary?.invalidRecords ?? 0) === 0;

    const hasFileErrors = files.some((f) => f.error);

    useEffect(() => {
      // Auto-run validation when returning from configuration with files ready
      if (
        shouldAutoValidate &&
        !validating &&
        files.length > 0 &&
        !hasFileErrors &&
        !validationReport
      ) {
        setShouldAutoValidate(false);
        runValidation(false);
      }
    }, [shouldAutoValidate, validating, hasFileErrors, validationReport, files, runValidation]);

    const hasStructuralErrors =
      !!validationReport &&
      (validationReport.structuralValidation?.errors || []).filter((e) => e.level === 'HIGH')
        .length > 0;
    const hasRowErrors =
      !!validationReport &&
      (validationReport.rowValidation?.errors || []).filter((e) => e.level === 'HIGH').length > 0;

    const hasInsertableRecords = (validationReport?.summary?.insertableRecords ?? 0) > 0;

    const canSkipInvalidRows =
      !!validationReport &&
      !canProcess &&
      !hasStructuralErrors &&
      hasRowErrors &&
      hasInsertableRecords;

    const notesAreValid = !notesRequired || reasonForMultipleUploads.trim() !== '';

    const sampleData = validationReport?.sampleData || [];

    const onSkipInvalidRows = useCallback(async () => {
      if (!canSkipInvalidRows) return;
      await runProcessing();
    }, [runProcessing, canSkipInvalidRows]);

    const onExportTransformedCsv = useCallback(
      async (rowExportType: PayrollRowExportType) => {
        if (files.length === 0) return;
        const encodedFiles = await formatFilesForProcessing(files);
        const res = await transformForExport.mutateAsync({
          agentId,
          files: encodedFiles,
          rowExportType,
        });
        const rows = res?.rows || [];
        exportFileValidationTransformedRowCsv(rows, rowExportType);
      },
      [files, agentId, transformForExport],
    );

    const getProcessingStatusColor = () => {
      if (canProcess) return 'text-emerald';
      if (hasStructuralErrors || hasRowErrors) return 'text-red-600';
      return 'text-amber';
    };

    const getProcessingStatusIcon = () => {
      if (canProcess) return <CheckCircle className='h-6 w-6 text-emerald' />;
      if (hasStructuralErrors || hasRowErrors)
        return <AlertCircle className='h-6 w-6 text-red-600' />;
      return <AlertTriangle className='h-6 w-6 text-amber' />;
    };

    const getProcessingStatusMessage = () => {
      if (canProcess) return 'Ready to Process';
      if (hasStructuralErrors) return 'Cannot Process - File & Structure Errors';
      if (hasRowErrors) return 'Cannot Process - Data Errors Found';
      return 'Can Process with Warnings';
    };

    const getProcessingDescription = () => {
      if (canProcess)
        return 'All validation checks passed. Your payroll data is ready to be processed.';
      if (hasStructuralErrors) {
        return 'File and configuration errors must be fixed before processing can continue.';
      }
      if (hasRowErrors) {
        return 'Data errors must be fixed before processing can continue.';
      }
      if (!hasInsertableRecords) {
        return 'No valid records were found that could be uploaded. Please fix errors and upload new files.';
      }
      return 'Low-severity warnings detected. You can proceed, but review is recommended.';
    };

    if (processingStatus === 'uploading') {
      return (
        <div className='fixed inset-0 bg-black/50 flex items-center justify-center z-50'>
          <Card className='w-96'>
            <CardContent className='p-8 text-center'>
              <div className='flex flex-col items-center gap-4'>
                <Loader2 className='h-12 w-12 animate-spin text-internal-brand-blue' />
                <div className='text-xl font-semibold text-internal-brand-navy'>
                  Processing Payroll Files
                </div>
                <div className='text-sm text-internal-brand-gray'>
                  Please wait while we process your payroll data...
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    if (processingStatus === 'failed') {
      return (
        <div className='fixed inset-0 bg-black/50 flex items-center justify-center z-50'>
          <Card className='w-96'>
            <CardContent className='p-8 text-center'>
              <div className='flex flex-col items-center gap-4'>
                <AlertCircle className='h-12 w-12 text-red-600' />
                <div className='text-xl font-semibold text-red-600'>Processing Failed</div>
                <div className='text-sm text-internal-brand-gray'>
                  There was an error processing your payroll data. Please try again or contact
                  support.
                </div>
                {uploadStatusMessage && (
                  <Alert className='w-full border-red-200 bg-red-50 mt-2 flex items-center'>
                    <AlertCircle className='h-4 w-4 text-red-600' />
                    <AlertDescription className='text-red-800 text-sm'>
                      {uploadStatusMessage}
                    </AlertDescription>
                  </Alert>
                )}
                <Button
                  onClick={() => {
                    setProcessingStatus(null);
                    onResetUpload();
                  }}
                  className='mt-4 bg-internal-brand-blue hover:bg-internal-brand-blue-dark text-white font-semibold'
                >
                  Try Again
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    return (
      <>
        <div className='flex flex-col gap-6 min-w-0'>
          <Collapsible open={uploadSectionOpen} onOpenChange={(open) => setUploadSectionOpen(open)}>
            <Card
              className={'border-2 border-internal-brand-blue hover:border-internal-brand-blue/90'}
            >
              <CollapsibleTrigger asChild>
                <CardHeader className='cursor-pointer bg-gradient-to-b from-internal-brand-blue to-internal-brand-blue/80 hover:bg-internal-brand-blue/90 transition-colors'>
                  <CardTitle className='flex items-center justify-between'>
                    <div className='flex items-center gap-3'>
                      <div className='p-2 bg-white/20 rounded-lg'>
                        <FileStack className='h-6 w-6 text-white' />
                      </div>
                      <div>
                        <div className='text-xl font-semibold text-white'>Upload Payroll Files</div>
                        <div className='text-sm text-white/90 font-normal'>
                          Upload and validate your payroll data for processing
                        </div>
                      </div>
                    </div>
                    <CollapseChevron open={uploadSectionOpen} className={'text-white/80'} />
                  </CardTitle>
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent className='p-6 bg-gray-200/60'>
                  {hasUploadedFiles && files.length === 0 && (
                    <Alert className='mb-6 border-internal-brand-blue bg-internal-brand-blue/5'>
                      <Sparkles className='h-4 w-4 text-internal-brand-blue' />
                      <AlertDescription className='text-internal-brand-blue-dark'>
                        <div className='flex items-center justify-between'>
                          <span>
                            You have uploaded files ready for validation. Upload new files to
                            replace them or continue with your existing files.
                          </span>
                        </div>
                      </AlertDescription>
                    </Alert>
                  )}
                  <FileUploadDropzone
                    files={files}
                    onFilesChange={handleFilesChange}
                    disabled={validating}
                  />
                  {hasUploadedFiles && isInternalUser && canMarkTestUpload && (
                    <div className='mt-6 flex items-center space-x-2'>
                      <Checkbox
                        id='test-upload'
                        checked={isTestUpload}
                        onCheckedChange={(checked) =>
                          setIsTestUpload(typeof checked === 'boolean' ? checked : false)
                        }
                        className='border-internal-brand-blue data-[state=checked]:bg-internal-brand-blue data-[state=checked]:text-white'
                      />
                      <Label
                        htmlFor='test-upload'
                        className='text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 text-internal-brand-navy cursor-pointer'
                      >
                        This is a test upload
                      </Label>
                    </div>
                  )}
                  <div className='mt-6 flex items-center justify-between'>
                    <div className='flex items-center gap-3'>
                      <Button
                        onClick={() => runValidation(false)}
                        disabled={files.length === 0 || validating || hasFileErrors}
                        className='bg-internal-brand-blue hover:bg-internal-brand-blue-dark text-white font-semibold shadow-sm'
                      >
                        {validating ? (
                          <Loader2 className='h-4 w-4 mr-2 animate-spin' />
                        ) : (
                          <CheckCircle className='h-4 w-4 mr-2' />
                        )}
                        Validate Files
                      </Button>
                      {!!validationReport &&
                        (canProcess ? (
                          <Badge className='bg-emerald hover:bg-emerald-600 text-white border-0 font-semibold px-3 py-1'>
                            <CheckCircle className='h-3 w-3 mr-1' /> Ready to process
                          </Badge>
                        ) : (
                          <Badge className='bg-amber hover:bg-amber-600 text-white border-0 font-semibold px-3 py-1'>
                            <AlertCircle className='h-3 w-3 mr-1' /> Fix errors to continue
                          </Badge>
                        ))}
                    </div>
                  </div>
                </CardContent>
              </CollapsibleContent>
            </Card>
          </Collapsible>
          {!!validationReport && (
            <>
              <Card className='border-gray-300 shadow-sm'>
                <CardHeader className='bg-gradient-to-b from-internal-brand-navy to-internal-brand-navy/90'>
                  <CardTitle className='flex items-center gap-2 text-white'>
                    <CheckCircle className='h-5 w-5 text-white' />
                    Validation Results
                  </CardTitle>
                </CardHeader>
                <CardContent className='p-6 bg-gray-200/60'>
                  <ValidationResults
                    report={validationReport}
                    canSkipInvalidRows={canSkipInvalidRows}
                    onSkipInvalidRows={onSkipInvalidRows}
                    onExportTransformedCsv={onExportTransformedCsv}
                    onConfigureUnmapped={onConfigureUnmapped}
                  />
                </CardContent>
              </Card>
              {sampleData.length > 0 && (
                <Card className='border-gray-200 shadow-sm'>
                  <CardHeader>
                    <div className='flex items-center justify-between'>
                      <CardTitle className='text-lg font-semibold text-internal-brand-navy flex items-center gap-2'>
                        <FileText className='h-5 w-5 text-internal-brand-blue' />
                        Data Preview
                      </CardTitle>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant='outline' size='sm' className='py-5 px-3 text-xs'>
                            <Download className='h-4 w-4 mr-2' /> Export full CSV
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align='end'>
                          {(
                            [
                              {exportType: 'ALL_ROWS', label: 'Export all rows'},
                              {exportType: 'VALID_ROWS', label: 'Export valid rows only'},
                              {exportType: 'INVALID_ROWS', label: 'Export invalid rows only'},
                            ] as const
                          ).map(({exportType, label}) => (
                            <DropdownMenuItem
                              key={exportType}
                              className={'cursor-pointer'}
                              onClick={() => onExportTransformedCsv(exportType)}
                            >
                              {label}
                            </DropdownMenuItem>
                          ))}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className='text-sm text-internal-brand-gray mb-4'>
                      First {Math.min(sampleData.length, 5)} rows from your data
                    </div>
                    <SampleDataTable sampleData={sampleData} maxRows={5} />
                  </CardContent>
                </Card>
              )}
              <Collapsible
                open={notesSectionOpen}
                onOpenChange={(open) => setNotesSectionOpen(open)}
              >
                <Card className='border-gray-200 shadow-sm'>
                  <CollapsibleTrigger asChild>
                    <CardHeader>
                      <CardTitle className='flex items-center justify-between gap-2 font-semibold text-internal-brand-navy'>
                        <div className={'text-lg flex items-center gap-2'}>
                          <NotebookPen className='h-5 w-5 text-internal-brand-blue' />
                          Upload Notes {notesRequired ? '' : '(Optional)'}
                        </div>
                        <CollapseChevron
                          open={notesSectionOpen}
                          className={'text-internal-brand-gray'}
                        />
                      </CardTitle>
                    </CardHeader>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <CardContent className='space-y-4'>
                      {notesRequired && (
                        <>
                          <div className='text-sm text-gray-600 font-semibold'>
                            Based on our records of previous uploads, we require additional context
                            for multiple payroll file uploads within the same pay period. Please
                            select a reason below for this additional upload.
                          </div>
                          <div>
                            <Select
                              value={reasonForMultipleUploads}
                              onValueChange={(v) => setReasonForMultipleUploads(v)}
                            >
                              <SelectTrigger className='h-10 border-gray-200 focus:border-internal-brand-blue focus:ring-internal-brand-blue'>
                                <SelectValue placeholder='Select a reason' />
                              </SelectTrigger>
                              <SelectContent>
                                {[
                                  'Updated Payroll Data',
                                  'Employee(s) Missing from Previous Upload',
                                  'File Formatting Update',
                                  'Payroll System Error',
                                  'Wrong Files Submitted',
                                  'Testing',
                                  'Other',
                                ].map((reason, idx) => (
                                  <SelectItem
                                    className={'font-medium text-sm text-gray-700'}
                                    key={idx}
                                    value={reason}
                                  >
                                    {reason}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </>
                      )}
                      <div>
                        <div className='text-sm font-medium text-gray-600 mb-2'>
                          Add any {notesRequired ? 'additional ' : ''}notes for this upload below
                        </div>
                        <Textarea
                          value={uploadNotes}
                          onChange={(e) => setUploadNotes(e.target.value)}
                          className='min-h-[100px]'
                          maxLength={1000}
                        />
                      </div>
                    </CardContent>
                  </CollapsibleContent>
                </Card>
              </Collapsible>
              <div className='space-y-6'>
                <Card className='border-gray-200 shadow-sm'>
                  <CardHeader>
                    <CardTitle className='text-lg font-semibold text-internal-brand-navy flex items-center gap-2'>
                      <Upload className='h-5 w-5 text-internal-brand-blue' />
                      Process Payroll Data
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className='flex items-center justify-between'>
                      <div className='flex items-center gap-4'>
                        {getProcessingStatusIcon()}
                        <div>
                          <div className={`text-xl font-semibold ${getProcessingStatusColor()}`}>
                            {getProcessingStatusMessage()}
                          </div>
                          <div className='text-internal-brand-gray mt-1'>
                            {getProcessingDescription()}
                          </div>
                        </div>
                      </div>
                      <div className='text-right'>
                        <div className='text-3xl font-bold text-internal-brand-navy'>
                          {validationReport.summary.totalRecords || 0}
                        </div>
                        <div className='text-sm text-internal-brand-gray'>Records to Process</div>
                      </div>
                    </div>
                    <div className='pt-4'>
                      <Separator />
                      {canSkipInvalidRows ? (
                        <div className={'mt-4'}>
                          <SkipInvalidAlert
                            onExportInvalidRows={() => onExportTransformedCsv('INVALID_ROWS')}
                            invalidRowCount={Number(validationReport.summary.invalidRecords || 0)}
                            onSkipInvalidRows={onSkipInvalidRows}
                          />
                        </div>
                      ) : (
                        <div className='mt-4 flex items-center justify-between'>
                          <Button
                            onClick={() => runProcessing()}
                            disabled={!canProcess || !notesAreValid}
                            className={`font-semibold shadow-sm ${
                              canProcess && notesAreValid
                                ? 'bg-internal-brand-blue hover:bg-internal-brand-blue-dark text-white'
                                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                            }`}
                          >
                            <Upload className='h-4 w-4 mr-2' />
                            Process Payroll Data
                          </Button>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </>
          )}
        </div>
        <FileIdValidationAlert
          fileIdValidation={fileIdValidation}
          onClose={onCloseFileIdValidationAlert}
          onSwapFileIds={applySwappedFileIds}
          onConfirm={() => {
            runValidation(true);
          }}
        />
      </>
    );
  },
);
